import { useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { useNavigate } from "react-router-dom";
import { redirectToLogin } from "../utils/auth";
import { useUser } from "../context/UserContext";

const tabs = [
  { heading: "Request Details", id: "requestDetails" },
  { heading: "Media Upload", id: "mediaUpload" },
];

// Mirrors the tag pill / dropdown look from the LOA mockup (purple chips, rounded-lg control).
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: "8px",
    borderColor: state.isFocused ? "#3e0084" : "#e0e0e0",
    boxShadow: "none",
    padding: "2px",
    minHeight: "44px",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#f4f0fa",
    borderRadius: "15px",
    border: "1px solid #dcd0f0",
    paddingLeft: "4px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#3e0084",
    fontWeight: "bold",
    fontSize: "12px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#3e0084",
    ":hover": { backgroundColor: "#dcd0f0", color: "#3e0084" },
  }),
};

export const LoaForm = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [title, setTitle] = useState("");
  const [assistants, setAssistants] = useState([]);
  const [org, setOrg] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const authToken = localStorage.getItem("authToken");
  const navigate = useNavigate();

  const { user } = useUser();
  const isAdmin = user.loaded ? !user.isPartner : false;

  const loadOptions = async (searchQuery, loadedOptions, { page }) => {
    if (!isAdmin) return;
    const limit = 50;
    const offset = (page - 1) * limit;

    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/getAllOrg?offset=${offset}&limit=${limit}&search=${encodeURIComponent(searchQuery)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      },
    );
    if (res.status === 401) { redirectToLogin(); return; }
    if (res.status === 500) { alert("Internal Server Error"); redirectToLogin(); return; }

    const data = await res.json();
    const options = data.organizations.map((orgName) => ({ label: orgName, value: orgName }));
    const hasMore = offset + limit < data.totalCount;
    return { options, hasMore, additional: { page: page + 1 } };
  };

  // Assistants (bots) come straight from JBM via the backend proxy — available to
  // every authenticated user (partners are org-scoped by their own token, like orgs).
  const loadAssistantOptions = async (searchQuery, loadedOptions, { page }) => {
    const limit = 50; // JBM caps a page at 50
    const offset = (page - 1) * limit;

    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/getAllBots?offset=${offset}&limit=${limit}&search=${encodeURIComponent(searchQuery)}&organization=${encodeURIComponent(org?.value || "")}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      },
    );
    if (res.status === 401) { redirectToLogin(); return { options: [], hasMore: false }; }
    if (!res.ok) { return { options: [], hasMore: false }; }

    const data = await res.json();
    // Show both the assistant name and its stable botId; store the combined
    // string so both survive a reload (the LOA `assistants` field is [String]).
    const options = (data.bots || []).map((b) => {
      const label = `${b.displayName} (${b.botId})`;
      return { label, value: label };
    });
    const hasMore = offset + limit < (data.totalCount || 0);
    return { options, hasMore, additional: { page: page + 1 } };
  };

  const fileSelectedHandler = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles((prevFiles) => [...prevFiles, ...files]);
  };

  const removeFileHandler = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const save = async () => {
    if (!title || assistants.length === 0) {
      alert("Please fill all required fields.");
      return;
    }
    if (isAdmin && !org) {
      alert("Please fill all required fields.");
      return;
    }

    setSubmitting(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("assistants", JSON.stringify(assistants.map((a) => a.value)));
    if (isAdmin) {
      formData.append("org", org.value);
    }
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/LOA`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        },
      );

      const result = await response.json();
      if (response.ok) {
        alert("LOA submitted successfully!");
        navigate("/LOA");
      } else {
        alert(result.error || "Something went wrong.");
      }
    } catch (error) {
      console.error("Error submitting LOA:", error);
      alert("An error occurred while saving the LOA.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col p-6 min-h-[calc(100vh-72px)]">
      {/* Tab Bar */}
      <div className="flex gap-8 border-b border-gray-200 pb-2.5 mb-5 relative">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(index)}
            className={`text-sm font-bold pb-1.5 ${
              activeTab === index ? "border-b-[3px] border-jiojbm50" : ""
            }`}
          >
            {tab.heading}
          </button>
        ))}
        <button
          type="button"
          className={`absolute right-0 -top-1 text-xs px-6 py-2 rounded-full font-bold text-white ${
            submitting ? "opacity-50 cursor-not-allowed bg-gray-400" : "bg-jiojbm50 hover:bg-jiojbm60"
          }`}
          onClick={submitting ? null : save}
          disabled={submitting}
        >
          {submitting ? "Saving..." : "Save"}
        </button>
      </div>

      {/* Tab: Request Details */}
      {activeTab === 0 && (
        <div className="flex flex-col">
          <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)] mb-5">
            <label className="text-xs font-semibold text-gray-800 mb-2 block">
              Title *
            </label>
            <input
              type="text"
              className="w-full p-3 border border-gray-200 rounded-lg text-sm outline-none focus:border-jiojbm50"
              placeholder="Add title here..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {isAdmin && (
            <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)] mb-5">
              <label className="text-xs font-semibold text-gray-800 mb-2 block">
                Organisation *
              </label>
              <AsyncPaginate
                value={org}
                loadOptions={loadOptions}
                onChange={setOrg}
                additional={{ page: 1 }}
                placeholder="Select Organisation"
                isSearchable
                debounceTimeout={300}
                styles={selectStyles}
                loadingMessage={() => "Loading Organisations, please wait..."}
              />
            </div>
          )}

          <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)] mb-5">
            <label className="text-xs font-semibold text-gray-800 mb-2 block">
              Assistant Mapping *
            </label>
            <p className="text-xs text-gray-500 mb-4">
              Search and select the Assistant IDs applicable to this LOA.
            </p>
            <AsyncPaginate
              isMulti
              value={assistants}
              loadOptions={loadAssistantOptions}
              onChange={(selected) => setAssistants(selected || [])}
              additional={{ page: 1 }}
              placeholder="Select Assistants to Map..."
              isSearchable
              debounceTimeout={300}
              styles={selectStyles}
              loadingMessage={() => "Loading Assistants, please wait..."}
            />
          </div>
        </div>
      )}

      {/* Tab: Media Upload */}
      {activeTab === 1 && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
          <input
            className="w-full border border-gray-200 rounded-lg text-sm p-3 outline-none focus:border-jiojbm50"
            type="file"
            onChange={fileSelectedHandler}
            multiple
            accept=".xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
          />
          {selectedFiles.length > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center border border-gray-200 rounded-lg p-3 text-sm"
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className="text-jiored1 hover:text-red-700 text-xs font-bold"
                    onClick={() => removeFileHandler(index)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
